/**
 * support_alerts_controller.js — the "you have a message" layer on the Chip
 * widget (Stimulus identifier support-alerts).
 *
 * Bound to the .pchat element of templates/components/ai_chat_widget.html
 * beside ai-chat and chat-tabs. It shows an unread count on the launcher and
 * the Support tab, a preview bubble beside the launcher when a message
 * arrives with the panel closed, and plays the message sound (a bird tweet, its
 * own sound and never one of the bell's), with a per-device mute.
 *
 * The server owns the numbers: GET /api/support/unread on connect, when the
 * tab is refocused, after the chat view marks a message read
 * ('support-chat:read') and when a staff inbox changes. Between fetches a
 * live message only adds one. What alerts is the pure shouldAlert() in
 * _support_alerts.js.
 *
 * Mute lives in localStorage under MUTE_KEY, written only here; every access
 * is wrapped and a blocked store means unmuted (the default).
 */
import { Controller } from "@hotwired/stimulus";
import { badgeText, isFromOtherSide, previewText, shouldAlert } from "./_support_alerts.js";

const MUTE_KEY = 'chippie.support.muted';
const BUBBLE_SHOWN_MS = 8000;

function readMuted() {
    try { return window.localStorage.getItem(MUTE_KEY) === '1'; } catch (_e) { return false; }
}

function storeMuted(muted) {
    try {
        if (muted) window.localStorage.setItem(MUTE_KEY, '1');
        else window.localStorage.removeItem(MUTE_KEY);
    } catch (_e) { /* per-device nicety only */ }
}

export default class extends Controller {
    static targets = ['launcherBadge', 'tabBadge', 'bubble', 'bubbleName', 'bubbleText', 'muteButton'];
    static values = { sound: String, me: Number, api: String };

    connect() {
        this._count = this._count || 0;
        this._onVisibility = () => { if (document.visibilityState === 'visible') this.refresh(); };
        document.addEventListener('visibilitychange', this._onVisibility);
        this._renderMute();
        this.refresh();
    }

    disconnect() {
        document.removeEventListener('visibilitychange', this._onVisibility);
        clearTimeout(this._bubbleTimer);
    }

    // Also data-action="support-chat:read->support-alerts#refresh".
    async refresh() {
        try {
            const response = await fetch(`${this.apiValue}/unread`, { credentials: 'same-origin' });
            if (!response.ok) return;
            const data = await response.json();
            this._isStaff = 'staff_attention_count' in data;
            this._count = data.customer_unread + (data.staff_attention_count || 0);
            this._renderBadges();
        } catch (_error) {
            /* offline: keep the last numbers */
        }
    }

    // data-action="chippie:support@document->support-alerts#receive"
    receive({ detail }) {
        const data = detail?.data || {};
        if (detail?.type === 'support_thread') {
            if (this._isStaff) this.refresh();
            return;
        }
        if (detail?.type !== 'support_message') return;
        const alert = shouldAlert({
            fromOtherSide: isFromOtherSide(data, this.meValue, this._isStaff),
            panelOpen: this.element.classList.contains('pchat--open'),
            supportTabActive: this._supportTabActive(),
            documentHidden: document.visibilityState === 'hidden',
        });
        if (alert.count) {
            this._count += 1;
            this._renderBadges();
        }
        if (alert.bubble) this._showBubble(data);
        if (alert.sound && !readMuted()) this._chime();
    }

    openFromBubble() {
        this._hideBubble();
        window.openSupport();
    }

    dismissBubble() {
        this._hideBubble();
    }

    toggleMute() {
        storeMuted(!readMuted());
        this._renderMute();
    }

    _supportTabActive() {
        const tab = this.tabBadgeTarget.closest('[role=tab]');
        return tab?.getAttribute('aria-selected') === 'true';
    }

    _renderBadges() {
        const text = badgeText(this._count);
        [this.launcherBadgeTarget, this.tabBadgeTarget].forEach((badge) => {
            badge.hidden = !text;
            badge.innerHTML = text ? `<span aria-hidden="true">${text}</span>`
                + `<span class="visually-hidden">${this._count} unread support messages</span>` : '';
        });
    }

    _showBubble(message) {
        const name = message.sender_first_name;
        this.bubbleNameTarget.textContent = this._isStaff ? (name || 'A customer')
            : (name ? `${name} from Chippie` : 'Chippie support');
        this.bubbleTextTarget.textContent = previewText(message);
        this.bubbleTarget.hidden = false;
        // Restart the pop-in for a bubble replacing a visible one.
        this.bubbleTarget.classList.remove('pchat-greeting--show');
        void this.bubbleTarget.offsetWidth;
        this.bubbleTarget.classList.add('pchat-greeting--show');
        clearTimeout(this._bubbleTimer);
        this._bubbleTimer = setTimeout(() => this._hideBubble(), BUBBLE_SHOWN_MS);
    }

    _hideBubble() {
        clearTimeout(this._bubbleTimer);
        this.bubbleTarget.hidden = true;
        this.bubbleTarget.classList.remove('pchat-greeting--show');
    }

    _chime() {
        if (!this._audio) {
            this._audio = new Audio(this.soundValue);
            this._audio.preload = 'auto';
        }
        try {
            this._audio.currentTime = 0;
            // Rejected before the page's first gesture (autoplay policy): stay silent.
            const playing = this._audio.play();
            if (playing && typeof playing.catch === 'function') playing.catch(() => {});
        } catch (_e) { /* no audio support */ }
    }

    // One mute button for customers and one above the staff inbox.
    _renderMute() {
        const muted = readMuted();
        const label = muted ? 'Turn message sound on' : 'Turn message sound off';
        this.muteButtonTargets.forEach((button) => {
            button.setAttribute('aria-pressed', String(muted));
            button.setAttribute('aria-label', label);
            button.title = label;
            button.querySelector('i').className = muted ? 'bi bi-bell-slash' : 'bi bi-bell';
        });
    }
}
